/**
 * Mobile Player Table - Free Agent Search
 * Wrapper around MobileDynastyTable with 2-row format
 */

import React, { useMemo } from 'react';
import { dynastyTheme, dynastyTileStyle } from '../../../../services/colorService';

import { mobileTableStyles, mobileTileStyle } from '../../../../services/mobileStyleService';
import { MobileDynastyTable } from '../../components/MobileDynastyTable';
import { createMobileHitterColumns, createMobilePitcherColumns } from './mobileTableColumns';
import { useMobileBatchSelection } from './MobileBatchSelectionProvider';
// 9o.6.e Commit 8: rolling row visibility + window-aware initial sort key.
import { STATS_KEY_FOR_RANGE } from '../../../../utils/playerStatsWindow';
// Bible § 14: derive column set from league_settings.scoring_categories.
import { getStatConfigs, DEFAULT_BATTING_STATS, DEFAULT_PITCHING_STATS } from '../../../../utils/statMapping';

const C = dynastyTheme.tokens.colors;

const MobilePlayerTable = ({ state, leagueId, league, isCommissionerMode, activeTeamName, activeTeamId, handlePlayerClick }) => {
  const {
    players,
    loading,
    backgroundLoading,
    activeTab,
    showAll,
    handleAddPlayer,
    addingPlayer,
    advancedFilters,
    showRecent = true,
  } = state;

  const { bulkMode, isPlayerSelected, togglePlayerSelection } = useMobileBatchSelection();

  // Stats window for rolling row + initial sort key.
  const dateRange = advancedFilters?.dateRange ?? 'season';
  const initialStatsSlot = STATS_KEY_FOR_RANGE[dateRange] || 'season';

  // Custom row styling for free-agent-search - match box score exactly
  // MUST be defined before any conditional returns (React Hooks rules)
  const getRowStyle = React.useCallback((item, itemIndex, rowType, rowIndex, defaultStyle) => {
    // Calculate global row index across all rows (not just per player)
    // Each player has 2 rows (season + rolling), so:
    // Player 0: rows 0, 1
    // Player 1: rows 2, 3
    // Player 2: rows 4, 5
    // etc.
    const globalRowIndex = (itemIndex * 2) + rowIndex;
    
    // Alternating colors on EVERY row (matching box score)
    // Even rows (0, 2, 4...): #1a1a1a (darker)
    // Odd rows (1, 3, 5...): neutral[800] (lighter)
    const rowBgColor = globalRowIndex % 2 === 0 ? mobileTableStyles.rows.even : mobileTableStyles.rows.odd;
    
    // Thin gray border between every row (matching box score)
    // No yellow lines - just subtle gray separators
    return {
      ...defaultStyle,
      backgroundColor: rowBgColor,
      borderBottom: mobileTableStyles.rows.border
    };
  }, []);

  // Bible § 14: build statConfigs from league_settings.scoring_categories so
  // the column factories can filter their stat columns to the league's actual
  // scoring set. Fall back to canonical defaults when league hasn't loaded.
  const statConfigs = useMemo(() => {
    const isPitcher = activeTab === 'pitching';
    const labels = league?.league_settings?.scoring_categories
      ? (isPitcher
          ? (league.league_settings.scoring_categories.pitching || DEFAULT_PITCHING_STATS)
          : (league.league_settings.scoring_categories.hitting || DEFAULT_BATTING_STATS))
      : (isPitcher ? DEFAULT_PITCHING_STATS : DEFAULT_BATTING_STATS);
    return getStatConfigs(labels, isPitcher);
  }, [activeTab, league]);

  // Create column definitions
  const columns = useMemo(() => {
    const columnConfig = {
      onAddPlayer: handleAddPlayer,
      addingPlayer,
      showAll,
      bulkMode,
      isPlayerSelected,
      togglePlayerSelection,
      isCommissionerMode,
      activeTeamName,
      myTeamId: activeTeamId,
      dateRange,
      onPlayerClick: handlePlayerClick,
      statConfigs,
    };

    return activeTab === 'hitting'
      ? createMobileHitterColumns(columnConfig)
      : createMobilePitcherColumns(columnConfig);
  }, [activeTab, showAll, bulkMode, handleAddPlayer, addingPlayer, isPlayerSelected, togglePlayerSelection, isCommissionerMode, activeTeamName, activeTeamId, dateRange, handlePlayerClick, statConfigs]);

  // Loading state — spinner shows for every initial-load refetch (tab change,
  // filter change, window change), not only on first mount. Pagination uses
  // `backgroundLoading` and renders the bottom indicator instead.
  if (loading) {
    return (
      <div style={{
        ...mobileTileStyle,
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 12px'
        }}></div>
        <p style={{ color: dynastyTheme.tokens.colors.neutral[400], fontSize: '13px' }}>
          Loading {activeTab}...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Empty state
  if (!loading && players.length === 0) {
    return (
      <div style={{
        ...mobileTileStyle,
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>
          🔍
        </div>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: C.white,
          marginBottom: '8px'
        }}>
          No {activeTab} found
        </h3>
        <p style={{
          fontSize: '13px',
          color: dynastyTheme.tokens.colors.neutral[400],
          marginBottom: '8px'
        }}>
          Try adjusting your filters or search term
        </p>
      </div>
    );
  }

  // Initial sort configuration — 9o.6.e Commit 8: targets the active stats window
  // so initial sort matches the displayed data (client column-click sort already
  // aligns via window-aware keys in mobileTableColumns).
  const initialSort = activeTab === 'hitting'
    ? { key: `stats.${initialStatsSlot}.hitting.home_runs`, direction: 'desc' }
    : { key: `stats.${initialStatsSlot}.pitching.strikeouts_pitched`, direction: 'desc' };

  return (
    <div style={{
      ...mobileTileStyle,
      // padding: top 0 lets the tab strip sit flush against the tile's top
      // edge — the canonical 8px gap above this tile comes from
      // MobileSearchControls' marginBottom, not from internal padding.
      padding: '0 15px 15px 15px',
      overflow: 'hidden',
      marginTop: '0',
      marginBottom: '15px'
    }}>
      {/* Tab Selector - Inside the box like box score */}
      <div style={{
        display: 'flex',
        borderBottom: mobileTableStyles.rows.border,
        marginBottom: '12px'
      }}>
        <button
          onClick={() => state.setActiveTab('hitting')}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: 'transparent',
            color: activeTab === 'hitting' ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[400],
            border: 'none',
            borderBottom: activeTab === 'hitting' ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Hitters {state.totalCount > 0 && activeTab === 'hitting' && `(${state.totalCount})`}
        </button>
        <button
          onClick={() => state.setActiveTab('pitching')}
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: 'transparent',
            color: activeTab === 'pitching' ? dynastyTheme.tokens.colors.primary : dynastyTheme.tokens.colors.neutral[400],
            border: 'none',
            borderBottom: activeTab === 'pitching' ? `2px solid ${dynastyTheme.tokens.colors.primary}` : '2px solid transparent',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Pitchers {state.totalCount > 0 && activeTab === 'pitching' && `(${state.totalCount})`}
        </button>
      </div>

      {/* Table wrapper with inner border (matching box score structure).
          overflowY is intentionally left at default (visible). A prior
          `overflowY: 'hidden'` broke `position: sticky` on the table's
          `<thead>` — `hidden` makes the ancestor a sticky containing block
          that never scrolls, so the sticky header was being clipped away
          as the user scrolled the page. With overflowY left at default,
          sticky binds to the next real scroll ancestor (the page body).
          The MobileDynastyTable's own `maxHeight` keeps the table from
          overflowing this wrapper vertically. */}
      <div
        style={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          marginTop: '8px',
          border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderRadius: '6px',
          isolation: 'isolate',
          padding: 0,
          width: '100%',
          position: 'relative'
        }}
      >
        <MobileDynastyTable
          data={players}
          columns={columns}
          tableType="player-list"
          rowsPerItem={showRecent ? 2 : 1}
          rowTypes={showRecent ? ['season', 'rolling'] : ['season']}
          maxHeight="calc(100vh - 280px)"
          stickyHeader={true}
          initialSort={initialSort}
          getRowStyle={getRowStyle}
          onRowClick={(item) => {
            const player = item.player;
            if (player) {
              handlePlayerClick?.(player);
            }
          }}
        />
      </div>

      {/* Background loading indicator — infinite-scroll pagination only.
          Initial-load refetches show the full spinner above; pagination
          flips `backgroundLoading` while keeping existing rows visible. */}
      {backgroundLoading && players.length > 0 && (
        <div style={{
          padding: '12px',
          textAlign: 'center',
          borderTop: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: dynastyTheme.tokens.colors.neutral[400],
            fontSize: '11px'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: `2px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderTop: `2px solid ${dynastyTheme.tokens.colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            Loading more...
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePlayerTable;
